import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Campaign, getCampaign } from "../../../helpers/campaigns_helper";
import {
  checkCampaignDeliverability,
  type DeliverabilityCheckResult,
} from "../../../helpers/deliverability_helper";
import SendCampaignForm from "./SendCampaignForm";
import PreflightPanel from "./PreflightPanel";

/**
 * Página dedicada de disparo — DOC 09 `/campaigns/:id/send`.
 */
const CampaignSendPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const campaignId = Number(id);

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightResult, setPreflightResult] =
    useState<DeliverabilityCheckResult | null>(null);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const [preflightDismissed, setPreflightDismissed] = useState(false);

  useEffect(() => {
    document.title = t("sending.page.documentTitle");
  }, [t]);

  useEffect(() => {
    if (!Number.isFinite(campaignId) || campaignId < 1) {
      setError(t("campaigns.error.loadOne"));
      setLoading(false);
      return;
    }
    setLoading(true);
    getCampaign(campaignId)
      .then((res) => {
        if (res?.success) {
          setCampaign(res.data);
          setError(null);
        } else {
          setError(t("campaigns.error.loadOne"));
        }
      })
      .catch((e: any) => {
        setError(e?.message || t("campaigns.error.loadOne"));
      })
      .finally(() => setLoading(false));
  }, [campaignId, t]);

  const runPreflight = useCallback(async () => {
    if (!campaign?.id) {
      return;
    }
    setPreflightLoading(true);
    setPreflightError(null);
    try {
      const res = await checkCampaignDeliverability(campaign.id, {
        subject: campaign.subject,
        html_body: campaign.html_body,
      });
      if (res?.success && res.data) {
        setPreflightResult(res.data);
      } else {
        setPreflightResult(null);
        setPreflightError(t("preflight.retry"));
      }
    } catch (e: any) {
      setPreflightResult(null);
      setPreflightError(e?.message || t("preflight.retry"));
    } finally {
      setPreflightLoading(false);
    }
  }, [campaign, t]);

  useEffect(() => {
    if (!campaign?.id) {
      return;
    }
    setPreflightDismissed(false);
    void runPreflight();
  }, [campaign?.id, runPreflight]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("sending.page.title")}
            pageTitle={t("campaigns.title")}
          />
          <div className="mb-3">
            <Button color="light" size="sm" tag={Link} to="/campaigns">
              {t("campaigns.form.backToList")}
            </Button>
          </div>
          <Row>
            <Col lg={8}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {campaign
                      ? t("sending.page.heading", { name: campaign.name })
                      : t("sending.page.title")}
                  </h5>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                    </div>
                  ) : error || !campaign ? (
                    <Alert color="danger">
                      {error || t("campaigns.error.loadOne")}
                    </Alert>
                  ) : (
                    <SendCampaignForm
                      campaign={campaign}
                      onCancel={() => navigate("/campaigns")}
                      onSuccess={(sendId) => navigate(`/sends/${sendId}`)}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg={4}>
              {!preflightDismissed && campaign ? (
                <Card className="mb-3">
                  <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                    <h6 className="mb-0">{t("preflight.title")}</h6>
                    <Button
                      color="link"
                      size="sm"
                      className="p-0"
                      type="button"
                      disabled={preflightLoading}
                      onClick={() => void runPreflight()}
                    >
                      {t("preflight.recheck")}
                    </Button>
                  </CardHeader>
                  <CardBody>
                    <PreflightPanel
                      result={preflightResult}
                      loading={preflightLoading}
                      error={preflightError}
                      onRecheck={() => void runPreflight()}
                      onSendAnyway={
                        preflightResult?.band === "risk"
                          ? () => setPreflightDismissed(true)
                          : undefined
                      }
                    />
                  </CardBody>
                </Card>
              ) : null}
              <Card>
                <CardBody>
                  <h6 className="mb-2">{t("sending.page.sideTitle")}</h6>
                  <p className="text-muted small mb-2">
                    {t("sending.page.sideHelp")}
                  </p>
                  <Button
                    color="soft-primary"
                    size="sm"
                    tag={Link}
                    to="/settings/sending"
                  >
                    {t("sending.toast.openSetup")}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer closeButton={false} limit={3} />
    </React.Fragment>
  );
};

export default CampaignSendPage;
